import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Store, ShieldCheck, ChevronDown, Trash2 } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { Star } from "@/components/landing/Logo";
import { useLang } from "@/i18n/LanguageContext";
import { supportContent } from "@/data/supportContent";

const ICONS = [Mail, Store, ShieldCheck];

const Accordion = ({ items, idPrefix }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="rounded-2xl border" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
          <button
            data-testid={`${idPrefix}-q-${i + 1}`}
            onClick={() => setOpen(open === i ? null : i)}
            className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
          >
            <span className="text-sm sm:text-base font-semibold">{item.q}</span>
            <ChevronDown size={17} className="shrink-0 transition-transform duration-300" style={{ color: "var(--camel)", transform: open === i ? "rotate(180deg)" : "none" }} />
          </button>
          {open === i && (
            <p className="px-6 pb-5 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }} data-testid={`${idPrefix}-a-${i + 1}`}>
              {item.a}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default function Support() {
  const { lang } = useLang();
  const c = supportContent[lang] || supportContent.de;

  useEffect(() => {
    if (window.location.hash === "#konto-loeschen") {
      setTimeout(() => document.getElementById("konto-loeschen")?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, []);

  return (
    <main style={{ background: "var(--surface)", color: "var(--on-surface)" }} className="min-h-screen">
      <Navbar />
      <div className="mx-auto max-w-4xl px-5 sm:px-8 pt-36 pb-24" data-testid="support-page">
        <Reveal>
          <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
            <Star size={11} /> {c.kicker}
          </p>
          <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight" data-testid="support-title">
            {c.title}
          </h1>
          <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>{c.sub}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
          {c.contacts.map(({ title, email, text }, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={email} delay={i * 0.1} className="rounded-3xl border p-7" style={{ background: "var(--surface-secondary)", borderColor: "var(--line)" }} data-testid={`support-contact-${i + 1}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ background: "var(--rose-soft)" }}>
                  <Icon size={19} strokeWidth={1.6} style={{ color: "var(--camel)" }} />
                </span>
                <h2 className="mt-4 font-serif-display text-xl font-semibold">{title}</h2>
                <a href={`mailto:${email}`} className="mt-2 block break-all text-sm font-medium" style={{ color: "var(--camel)" }}>{email}</a>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{text}</p>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-20">
          <h2 className="font-serif-display text-3xl font-semibold" data-testid="support-app-faq-title">{c.appFaqTitle}</h2>
          <div className="mt-7"><Accordion items={c.appFaq} idPrefix="app-faq" /></div>
        </Reveal>

        <Reveal className="mt-16">
          <h2 className="font-serif-display text-3xl font-semibold" data-testid="support-b2b-faq-title">{c.b2bFaqTitle}</h2>
          <div className="mt-7"><Accordion items={c.b2bFaq} idPrefix="b2b-faq" /></div>
        </Reveal>

        <Reveal className="mt-20">
          <section id="konto-loeschen" className="rounded-3xl border-2 p-8 sm:p-10" style={{ borderColor: "var(--gold)", background: "var(--surface-secondary)" }} data-testid="support-delete-account">
            <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
              <Trash2 size={13} /> {c.del.kicker}
            </p>
            <h2 className="mt-4 font-serif-display text-3xl font-semibold">{c.del.title}</h2>
            <p className="mt-4 text-sm sm:text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{c.del.intro}</p>
            <ol className="mt-5 list-decimal space-y-3 pl-5 text-sm sm:text-base leading-relaxed">
              <li>{c.del.step1a}<a href="mailto:privacy@mira-mirror.ai" className="font-medium" style={{ color: "var(--camel)" }}>privacy@mira-mirror.ai</a>{c.del.step1b}<strong>{c.del.step1subject}</strong>{c.del.step1c}</li>
              <li>{c.del.step2}</li>
            </ol>
            <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{c.del.note}</p>
          </section>
        </Reveal>

        <Reveal className="mt-14">
          <p className="border-t pt-7 text-center text-xs leading-relaxed" style={{ borderColor: "var(--line)", color: "var(--muted-warm)" }}>
            {c.footerLine} ·{" "}
            <Link to="/datenschutz" className="gold-underline" style={{ color: "var(--camel)" }}>{c.privacy}</Link> ·{" "}
            <Link to="/impressum" className="gold-underline" style={{ color: "var(--camel)" }}>{c.imprint}</Link>
          </p>
        </Reveal>
      </div>
      <Footer />
    </main>
  );
}
